import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  deleteCaseStep,
  reorderCaseSteps,
  updateCaseStep,
} from "@/features/cases/api";
import { TaskFormDialog } from "@/features/cases/components/task-form-dialog";
import type { Case, CaseStep } from "@/features/cases/types";

interface CaseStepsPanelProps {
  caseRecord: Case;
  /** Refetch the case after any step change (keeps counts/next-due fresh). */
  onChanged: () => void;
  /** Called when the last open step is completed, to offer "resolve case?". */
  onAllStepsComplete: () => void;
}

/**
 * Workflow-steps panel for the case detail page: complete/uncomplete, add,
 * edit (description + due date), reorder (accessible move up/down), delete.
 * Steps are always rendered in `position` order (backend sorts them).
 */
export function CaseStepsPanel({
  caseRecord,
  onChanged,
  onAllStepsComplete,
}: CaseStepsPanelProps) {
  const caseId = caseRecord.id;
  const steps = caseRecord.steps;
  const [dialog, setDialog] = useState<{ step: CaseStep | null } | null>(null);
  const [deleting, setDeleting] = useState<CaseStep | null>(null);
  const [reordering, setReordering] = useState(false);

  async function toggle(step: CaseStep) {
    const wasLastOpen =
      !step.isCompleted && steps.filter((s) => !s.isCompleted).length === 1;
    try {
      await updateCaseStep(caseId, step.id, { is_completed: !step.isCompleted });
      onChanged();
      if (wasLastOpen) onAllStepsComplete();
    } catch {
      toast.error("Couldn't update step.");
    }
  }

  async function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= steps.length || reordering) return;
    const ids = steps.map((s) => s.id);
    // Swap the two positions (bounds guaranteed by the guard above).
    const a = ids[index]!;
    ids[index] = ids[target]!;
    ids[target] = a;
    setReordering(true);
    try {
      await reorderCaseSteps(caseId, ids);
      onChanged();
    } catch {
      toast.error("Couldn't reorder steps.");
    } finally {
      setReordering(false);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteCaseStep(caseId, deleting.id);
      onChanged();
    } catch {
      toast.error("Couldn't remove step.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          {steps.length === 0
            ? "No steps yet."
            : `${steps.filter((s) => !s.isCompleted).length} of ${steps.length} open`}
        </p>
        <Button size="sm" onClick={() => setDialog({ step: null })}>
          <Plus className="size-4" /> Add Step
        </Button>
      </div>

      {steps.length > 0 && (
        <ul className="flex flex-col gap-1">
          {steps.map((step, index) => (
            <li
              key={step.id}
              className="hover:bg-muted/40 flex items-center gap-3 rounded-md px-2 py-2"
            >
              <Checkbox
                checked={step.isCompleted}
                aria-label={`Mark "${step.description}" ${step.isCompleted ? "incomplete" : "complete"}`}
                onCheckedChange={() => void toggle(step)}
              />
              <div className="flex-1">
                <span
                  className={
                    step.isCompleted
                      ? "text-muted-foreground text-sm line-through"
                      : "text-foreground text-sm"
                  }
                >
                  {step.description}
                </span>
                {step.dueDate && (
                  <span className="text-muted-foreground ml-2 text-xs">
                    due {step.dueDate}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Move "${step.description}" up`}
                disabled={index === 0 || reordering}
                onClick={() => void move(index, -1)}
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Move "${step.description}" down`}
                disabled={index === steps.length - 1 || reordering}
                onClick={() => void move(index, 1)}
              >
                <ArrowDown className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit "${step.description}"`}
                onClick={() => setDialog({ step })}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete "${step.description}"`}
                onClick={() => setDeleting(step)}
              >
                <Trash2 className="text-destructive size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {dialog && (
        <TaskFormDialog
          key={dialog.step?.id ?? "new"}
          caseId={caseId}
          step={dialog.step}
          nextPosition={steps.length}
          open
          onOpenChange={(open) => !open && setDialog(null)}
          onSaved={onChanged}
        />
      )}

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove step?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes "{deleting?.description}" from the case.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
